/**
 * 🏅 Community Ticker — horizontally scrolling marquee of recent community
 * events (KILLING BLOW etc). Each event scrolls **across once** then the
 * banner hides until a brand-new event arrives. (No infinite loop.)
 *
 * Polls /api/community/banners every 60s. Already-shown event_ids are
 * tracked in sessionStorage so they don't re-scroll on every poll.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 60_000;
const SHOWN_KEY = "omni:ticker-shown-ids";

const RARITY_EMOJI = {
  common:    "🤍",
  uncommon:  "💚",
  rare:      "💙",
  epic:      "💜",
  legendary: "🧡",
  mythic:    "❤️‍🔥",
  exotic:    "🌈",
  omni:      "✨",
};

function loadShown() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SHOWN_KEY) || "[]"));
  } catch { return new Set(); }
}
function saveShown(set) {
  try {
    sessionStorage.setItem(SHOWN_KEY, JSON.stringify([...set]));
  } catch { /* ignore */ }
}

export default function CommunityBanner() {
  const [events, setEvents] = useState([]);
  const shownRef = useRef(loadShown());

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/community/banners`);
        if (cancelled) return;
        const all = r.data?.events || [];
        // Only show events we haven't scrolled yet this session.
        const fresh = all.filter((e) => !shownRef.current.has(e.event_id));
        setEvents(fresh);
      } catch { /* silent */ }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (events.length === 0) return null;

  // Scroll speed scales with item count so a 1-item ticker doesn't whip past
  const durationS = Math.max(18, events.length * 12);
  const hasFirstEver = events.some((e) => e.is_first);

  // After the single scroll completes, mark these event_ids as shown so they
  // never re-scroll this session and hide the banner.
  const onAnimEnd = () => {
    events.forEach((e) => shownRef.current.add(e.event_id));
    saveShown(shownRef.current);
    setEvents([]);
  };

  return (
    <div
      data-testid="community-banner"
      className={`relative w-full overflow-hidden border-b ${
        hasFirstEver
          ? "bg-gradient-to-r from-[#1a0500] via-[#3F0F0F] to-[#1a0500] border-[#FCD34D]/40"
          : "bg-gradient-to-r from-[#0F0F0F] via-[#1A0A0A] to-[#0F0F0F] border-zinc-800"
      }`}
    >
      <div
        className="flex whitespace-nowrap arena-ticker-scroll"
        style={{ animationDuration: `${durationS}s` }}
        onAnimationEnd={onAnimEnd}
      >
        {events.map((ev, i) => (
          <TickerItem key={`${ev.event_id}-${i}`} ev={ev} />
        ))}
      </div>
    </div>
  );
}

function TickerItem({ ev }) {
  if (ev.type !== "killing_blow") return null;
  const who = ev.handle ? `@${ev.handle}` : ev.display_name;
  const eggBadges = (ev.rewards?.egg_rarities || []).map((r) => RARITY_EMOJI[r] || "🥚").join(" ");
  const isFirst = !!ev.is_first;

  return (
    <span
      className="inline-flex items-center gap-2 px-6 py-1.5 text-xs sm:text-sm shrink-0"
      data-testid={`ticker-item-${ev.event_id}`}
    >
      {isFirst && (
        <span
          className="text-[9px] uppercase tracking-[0.2em] font-black bg-[#FCD34D] text-black px-1.5 py-0.5 arena-killing-blow-shimmer"
          data-testid="ticker-first-badge"
        >
          🏆 ROOKIE OF THE PHOENIX
        </span>
      )}
      <span className="text-base">💥</span>
      <span className="font-bold text-[#FCD34D]">{who}</span>
      <span className="text-neutral-400">landed a</span>
      <span className="font-black uppercase tracking-widest text-[#DC2626]">KILLING BLOW</span>
      <span className="text-neutral-400">on</span>
      <span className="font-bold text-white">{ev.boss_icon} {ev.boss_name}</span>
      <span className="text-neutral-600">·</span>
      <span className="font-mono tabular-nums text-[#FCD34D]">+{ev.rewards?.pt || 0} 🔥</span>
      <span className="font-mono tabular-nums text-[#FCD34D]">+{ev.rewards?.od || 0} 🪙</span>
      {eggBadges && <span className="text-base">{eggBadges}</span>}
      <span className="text-zinc-700 mx-4">✦</span>
    </span>
  );
}
